import {
  Dialog,
  DialogProps,
  LoadingIndicator,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import {
  useCreateEmailTemplateStore,
  useEmailTemplatesStore,
} from "../../stores/Hooks";
import { DialogSuccessContent } from "../../../../components/DialogSuccessContent";
import { DialogErrorContent } from "../../../../components/DialogErrorContent";
import { CreateEmailTemplateDrawerContent } from "./contents/CreateEmailTemplateDrawerContent";
import { NonDismissibleDrawer } from "../../../../components/drawer/NonDismissibleDrawer";

const CREATE_EMAIL_TEMPLATE_DRAWER_WIDTH = "1024px";

interface CreateEmailTemplateDrawerProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface DialogType extends DialogProps {
  description: string;
  isError?: boolean;
}

export const CreateEmailTemplateDrawer: React.FC<CreateEmailTemplateDrawerProps> =
  observer(({ open, setOpen }) => {
    const [openSuccessMessageDialog, setOpenSuccessMessageDialog] =
      useState(false);
    const [
      openProjectArchivedErrorMessageDialog,
      setOpenProjectArchivedErrorMessageDialog,
    ] = useState(false);
    const [openErrorMessageDialog, setOpenErrorMessageDialog] = useState(false);

    const { t } = useTranslation();
    const tokens = useFoundationColorTokens();
    const typography = useTypography();
    const spacing = useSpacing();
    const emailTemplatesStore = useEmailTemplatesStore();
    const createEmailTemplateStore = useCreateEmailTemplateStore();

    const dialogs: DialogType[] = [
      // Successful message dialog
      {
        open: openSuccessMessageDialog,
        title: t("emailTemplates.createSuccessfulDialogTitle"),
        onSecondaryButtonClick: (): void => {
          setOpenSuccessMessageDialog(false);
          emailTemplatesStore.getEmailTemplates();
        },
        description: t("emailTemplates.createSuccessfulDialogDescription"),
      },
      // Project archived error message dialog
      {
        open: openProjectArchivedErrorMessageDialog,
        title: t("common.projectAlreadyArchived"),
        onSecondaryButtonClick: (): void => {
          emailTemplatesStore.getEmailTemplates();
          setOpenProjectArchivedErrorMessageDialog(false);
        },
        description: t("common.projectAlreadyArchivedErrorText"),
        isError: true,
      },
      // Unexpected error message dialog
      {
        open: openErrorMessageDialog,
        title: t("emailTemplates.createErrorDialogTitle"),
        onSecondaryButtonClick: () => setOpenErrorMessageDialog(false),
        description: t("emailTemplates.createErrorDialogDescription"),
        isError: true,
      },
    ];

    const handleDrawerClose = (): void => {
      setOpen(false);
    };

    return (
      <>
        <NonDismissibleDrawer
          open={open}
          onClose={handleDrawerClose}
          anchor="right"
          slotProps={{
            paper: {
              sx: {
                width: "100%",
                maxWidth: CREATE_EMAIL_TEMPLATE_DRAWER_WIDTH,
              },
            },
          }}
        >
          <CreateEmailTemplateDrawerContent
            handleDrawerClose={handleDrawerClose}
            setOpenSuccessMessageDialog={setOpenSuccessMessageDialog}
            setOpenErrorMessageDialog={setOpenErrorMessageDialog}
            setOpenProjectArchivedErrorMessageDialog={
              setOpenProjectArchivedErrorMessageDialog
            }
          />
        </NonDismissibleDrawer>

        <LoadingIndicator
          isLoading={createEmailTemplateStore.loadingCreateEmailTemplate}
        />

        {dialogs.map(({ description, isError, ...dialogProps }, index) => (
          <Dialog
            key={index}
            secondaryButtonText={t("common.done")}
            {...dialogProps}
            disableBackdropClick={true}
          >
            {isError ? (
              <DialogErrorContent
                spacing={spacing}
                typography={typography}
                tokens={tokens}
                errorText={description}
              />
            ) : (
              <DialogSuccessContent
                spacing={spacing}
                typography={typography}
                tokens={tokens}
                description={description}
              />
            )}
          </Dialog>
        ))}
      </>
    );
  });
